import React, {
  ChangeEvent,
  DragEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import { MAX_SIZE_PDF } from '../../constants/constants';
import { firebase } from '../../firebase';
import withAuthorization from '../../session/with_authorization';
import { checkFileExtension, formatBytes, showAlert } from '../../utils/utils';
import Navigation from '../../components/header/Navigation';
import LoadingButton from '../../components/ui/button/LoadingButton';
import Select from '../../components/ui/input/Select';
import { postPdf } from './apis/fileUpload';
import { getPersonalPodcasts } from './apis/user';
import FileUploader from './components/FileUploader';
import { PersonalPodcast } from './types/podcast';
import { LinkContainer } from 'react-router-bootstrap';
import {
  isUserStatusFree,
  isUserStatusSubscribed
} from '../../utils/CheckUserStatus';
import {
  isSubscriptionTypeBasic,
  isSubscriptionTypeElite,
  isSubscriptionTypePro
} from '../../utils/CheckSubscriptionType';

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  margin: auto;
  padding: 0 16px 84px 16px;
  max-width: 600px;
  width: 100%;
  height: 100vh;
`;

const SelectWrapper = styled.div`
  margin-bottom: 20px;
`;

const ButtonWrapper = styled.div`
  margin-top: 20px;

  & button {
    border-radius: 20px !important;
  }
`;

const FileUpload: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [podcastId, setPodcastId] = useState('');
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [personalPodcasts, setPersonalPodcasts] = useState<PersonalPodcast[]>(
    []
  );

  const fileInputRef = useRef<HTMLInputElement>(null);

  const validExtensions: string[] = ['.rtf', '.pdf', '.docx', '.txt'];

  const podcastOptions: { label: string; value: string }[] = useMemo(
    () =>
      personalPodcasts.map((podcast) => ({
        label: podcast.name,
        value: podcast.podcastId
      })),
    [personalPodcasts]
  );

  useEffect(() => {
    const loadPersonalPodcasts = async () => {
      try {
        const currentUser = firebase.auth.currentUser;
        if (!currentUser) return;

        const token = await currentUser.getIdToken();
        const response = await getPersonalPodcasts(token);
        setPersonalPodcasts(response.data);

        // If the user has only one personal podcast then the drop down is not displayed
        if (response.data.length === 1) {
          setPodcastId(response.data[0].podcastId);
        }
      } catch (error) {
        console.error(error);
      }
    };

    loadPersonalPodcasts();
  }, [firebase.auth.currentUser]);

  const handleChangePodcast = (value: string) => {
    setPodcastId(value);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDropFile = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setDragging(false);
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
    }
  };

  const handleUploadFile = async () => {
    const podcast = personalPodcasts.find(
      (podcast) => podcast.podcastId === podcastId
    );
    if (!podcast) return;

    const currentUser = firebase.auth.currentUser;
    if (!currentUser || !file) return;

    const token = await currentUser.getIdToken();
    const formData = new FormData();
    formData.append('pdfFile', file);

    if (!checkFileExtension(file.name, validExtensions)) {
      showAlert(
        'Format type',
        `Please upload a file with one of the following extensions: ${validExtensions.join(
          ', '
        )}`
      );
    } else if (file.size > MAX_SIZE_PDF) {
      const size = formatBytes(MAX_SIZE_PDF);
      showAlert(
        'Maximum size',
        `The file you are trying to upload exceeds the maximum file size limit of ${size}.`
      );
    } else {
      setLoading(true);
      try {
        const response = await postPdf(
          formData,
          podcastId,
          podcast.feedUrl,
          token
        );
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        if (personalPodcasts.length > 1) {
          setPodcastId('');
        }

        const title =
          response.data.status === 'successful'
            ? 'Success'
            : 'Operation Failed';
        showAlert(title, response.data.message);
      } catch (error) {
        showAlert('Cannot upload file', 'Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <Navigation />
      <Container>
        {personalPodcasts.length > 1 && (
          <SelectWrapper>
            <Select
              label="Podcast"
              value={podcastId}
              onChange={handleChangePodcast}
              options={podcastOptions}
              placeholder="Select..."
            />
          </SelectWrapper>
        )}
        <FileUploader
          ref={fileInputRef}
          file={file}
          onDrop={handleDropFile}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onFileSelected={handleFileSelect}
        />
        <ButtonWrapper>
          <LoadingButton
            isLoading={loading}
            text="Upload"
            onClick={handleUploadFile}
            disabled={file === null || !podcastId || loading}
          />
        </ButtonWrapper>
        <LinkContainer exact to="/dashboard" style={{ marginTop: 24 }}>
          <a href="#">Cancel</a>
        </LinkContainer>
      </Container>
    </>
  );
};

const authCondition = (
  authUser: any,
  isPersonalPodcastOwner: boolean | undefined
) =>
  !!authUser &&
  isPersonalPodcastOwner &&
  (isUserStatusSubscribed(authUser.userStatus) ||
    isUserStatusFree(authUser.userStatus));
// && (isSubscriptionTypeElite(authUser.subscriptionType) ||
// authUser.subscriptionType === undefined);

export default withAuthorization(authCondition)(FileUpload);
